import { Ace } from 'ace-builds';
import { useCallback } from 'react';
import { useEventCallback } from '../../../../../../../utils/hooks/useEventCallback';
import { DatasetColumn } from '../../../../../../datasets/types';
import { VariablesMap } from '../types';

type AggregateColumn = DatasetColumn & { samples?: string[] };

type UseAggregateCompletersParams = {
  variables: VariablesMap;
  columns: AggregateColumn[];
};

const buildVariableCompletions = (variables: VariablesMap): Ace.Completion[] =>
  Object.entries(variables).map(([name, { __formula }]) => ({
    caption: `$$${name}`,
    value: `$$${name}`,
    meta: 'variable',
    docText: __formula || undefined,
    score: 1000,
  }));

const buildColumnCompletions = (columns: AggregateColumn[]): Ace.Completion[] =>
  columns.map(column => {
    const docParts = [
      column.name && column.name !== column.datasetAttributeName
        ? column.name
        : null,
      column.samples?.length
        ? `e.g. ${column.samples.slice(0, 3).join(', ')}`
        : null,
    ].filter(Boolean);

    return {
      caption: `$${column.datasetAttributeName}`,
      value: `$${column.datasetAttributeName}`,
      meta: column.type || 'column',
      docText: docParts.length ? docParts.join(' — ') : undefined,
    };
  });

const isValidJson = (text: string): boolean => {
  try {
    JSON.parse(text);
    return true;
  } catch {
    return false;
  }
};

const getTextAfterCursor = (
  session: Ace.EditSession,
  position: Ace.Point
): string => {
  const lines = session.getLines(position.row, session.getLength() - 1);
  lines[0] = (lines[0] ?? '').slice(position.column);
  return lines.join('\n');
};

const findClosingQuoteIndex = (text: string): number => {
  let escaped = false;
  for (let i = 0; i < text.length; i += 1) {
    const char = text[i];
    if (escaped) {
      escaped = false;
    } else if (char === '\\') {
      escaped = true;
    } else if (char === '"') {
      return i;
    }
  }
  return -1;
};

/**
 * Whether the string currently being typed is a JSON object key. In valid JSON
 * a key is exactly a string immediately followed by `:`, so we close the
 * current string (the next quote, JSON strings cannot span newlines) and check
 * whether the next non-whitespace character is a colon. Mongo references
 * (`$col`, `$$var`) only appear in values, so key positions get no suggestions.
 */
const isKeyPosition = (textAfterCursor: string): boolean => {
  const closingQuote = findClosingQuoteIndex(textAfterCursor);
  if (closingQuote === -1) return false;

  return textAfterCursor
    .slice(closingQuote + 1)
    .trimStart()
    .startsWith(':');
};

/**
 * Adds Mongo-aware autocompletion to the aggregate JSON editor:
 * - `$$` suggests the lookup's field variables (with their formula as the hint)
 * - `$` suggests the main dataset's columns (Mongo field references)
 *
 * Suggestions are only offered when the document is valid JSON and the cursor
 * sits inside a value (not a key). Requiring valid JSON keeps the key/value
 * check trivial and means an unclosed quote simply yields no suggestions, which
 * matches the usual "type both quotes, then fill in the string" editing flow.
 *
 * Ace's default completion-prefix regex already includes `$`, so we only need
 * to inspect the prefix and position. `getCompletions` is an event callback so
 * it stays referentially stable while always reading the latest `variables` and
 * `columns` from props, which means the completer never needs re-registering.
 */
export const useAggregateCompleters = ({
  variables,
  columns,
}: UseAggregateCompletersParams) => {
  const getCompletions = useEventCallback<
    Parameters<NonNullable<Ace.Completer['getCompletions']>>,
    void
  >((editor, session, position, prefix, callback) => {
    if (!prefix.startsWith('$')) {
      callback(null, []);
      return;
    }

    if (!isValidJson(editor.getValue())) {
      callback(null, []);
      return;
    }

    // TODO: Maybe add suggestions for MQL pipeline keys?
    if (isKeyPosition(getTextAfterCursor(session, position))) {
      callback(null, []);
      return;
    }

    callback(null, [
      ...buildVariableCompletions(variables),
      ...buildColumnCompletions(columns),
    ]);
  });

  const onLoad = useCallback(
    (editor: Ace.Editor) => {
      // oxlint-disable-next-line no-param-reassign -- Ace API requires mutating editor.completers
      editor.completers = [{ getCompletions }];
    },
    [getCompletions]
  );

  return { onLoad };
};
